import * as React from 'react';

/**
 * Size options for the Progress component
 */
export type ProgressSize = 'thin' | 'default';

/**
 * Variant options for the Progress component
 */
export type ProgressVariant = 'primary' | 'success' | 'warning' | 'error' | 'info';

/**
 * Base props for the Progress component
 */
interface ProgressBaseProps {
  /**
   * Progress value from 0 to 100.
   * When undefined, the progress bar shows an indeterminate animation.
   */
  value?: number;
  /**
   * The size of the progress bar.
   * - 'thin': 4px height
   * - 'default': 8px height
   * @default 'default'
   */
  size?: ProgressSize;
  /**
   * The color variant of the progress bar.
   * @default 'primary'
   */
  variant?: ProgressVariant;
  /**
   * Whether to show the percentage value as text.
   * Only applies when value is defined (determinate mode).
   * @default false
   */
  showValue?: boolean;
  /**
   * Additional CSS classes.
   */
  className?: string;
}

/**
 * ProgressProps extends HTML div attributes
 */
export type ProgressProps = ProgressBaseProps &
  Omit<React.HTMLAttributes<HTMLDivElement>, keyof ProgressBaseProps>;

/**
 * Height values for each size
 */
const sizeMap: Record<ProgressSize, string> = {
  thin: 'h-1', // 4px
  default: 'h-2', // 8px
};

/**
 * Color classes for each variant (fill color)
 */
const variantMap: Record<ProgressVariant, string> = {
  primary: 'bg-primary-600',
  success: 'bg-success-700',
  warning: 'bg-warning-700',
  error: 'bg-error-700',
  info: 'bg-info-700',
};

/**
 * Text color classes for each variant (percentage display)
 */
const textVariantMap: Record<ProgressVariant, string> = {
  primary: 'text-primary-700',
  success: 'text-success-800',
  warning: 'text-warning-800',
  error: 'text-error-800',
  info: 'text-info-800',
};

/**
 * Progress component for displaying progress of tasks or loading states.
 *
 * Designed for insurance industry applications where clear progress indicators
 * help users understand system state during data processing, file uploads,
 * and multi-step workflows.
 *
 * Features:
 * - Determinate mode: Shows specific progress with value 0-100
 * - Indeterminate mode: Oscillating animation for unknown duration tasks
 * - Two size options: thin (4px) and default (8px)
 * - Five color variants: primary, success, warning, error, info
 * - Optional percentage display
 * - Respects prefers-reduced-motion preference
 * - Full accessibility support with ARIA attributes
 *
 * @example
 * // Basic determinate progress
 * <Progress value={50} />
 *
 * @example
 * // Indeterminate progress (no value)
 * <Progress />
 *
 * @example
 * // With percentage display
 * <Progress value={75} showValue />
 *
 * @example
 * // Different sizes and variants
 * <Progress value={60} size="thin" variant="success" />
 *
 * @example
 * // File upload progress
 * <Progress value={uploadProgress} variant="info" showValue />
 */
const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      value,
      size = 'default',
      variant = 'primary',
      showValue = false,
      className = '',
      ...props
    },
    ref
  ) => {
    // Determine if we're in indeterminate mode
    const isIndeterminate = value === undefined;

    // Clamp value to 0-100 range
    const clampedValue = isIndeterminate
      ? undefined
      : Math.min(100, Math.max(0, value));

    // Build container classes
    const containerClasses = [
      'w-full',
      showValue && !isIndeterminate ? 'flex items-center gap-3' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Build track classes
    const trackClasses = [
      'w-full',
      'bg-secondary-200',
      'rounded-full',
      'overflow-hidden',
      sizeMap[size],
    ].join(' ');

    // Build fill classes
    const fillClasses = [
      'h-full',
      'rounded-full',
      'transition-all',
      'duration-300',
      'ease-out',
      variantMap[variant],
      isIndeterminate ? 'animate-progress-indeterminate motion-reduce:animate-none' : '',
    ]
      .filter(Boolean)
      .join(' ');

    // Calculate fill width for determinate mode
    const fillStyle: React.CSSProperties = isIndeterminate
      ? { width: '40%' } // Width of the oscillating bar
      : { width: `${clampedValue}%` };

    // ARIA attributes
    const ariaProps = {
      role: 'progressbar' as const,
      'aria-valuemin': 0,
      'aria-valuemax': 100,
      ...(isIndeterminate
        ? { 'aria-valuetext': 'Loading...' }
        : { 'aria-valuenow': clampedValue }),
    };

    return (
      <div ref={ref} className={containerClasses} {...props}>
        <div className={showValue && !isIndeterminate ? 'flex-1' : 'w-full'}>
          <div className={trackClasses} {...ariaProps}>
            <div className={fillClasses} style={fillStyle} />
          </div>
        </div>
        {showValue && !isIndeterminate && (
          <span
            className={`text-sm font-medium tabular-nums ${textVariantMap[variant]}`}
            aria-hidden="true"
          >
            {clampedValue}%
          </span>
        )}
      </div>
    );
  }
);

Progress.displayName = 'Progress';

export { Progress };
